import { autoinject } from "aurelia-dependency-injection";
import { buildQueryString } from "aurelia-framework";
import { HttpClient, RequestBuilder, RequestMessageProcessor, RequestMessage } from "aurelia-http-client";
import { CookieHelper } from "../../helpers/cookie-helper";
import { FileHelper } from "../../helpers/file-helper";
import { CacheClientService, CacheKeys, CacheLocation } from "./client-caching.service";
import { ConfigService } from "./config.service";
import { ConfigurationService } from "./configuration.service";
import { NotificationClientService, NotificationType } from "./notification-client.service";
import { RequestConstants } from "../../constants/request.constants";
import { CloneHelper } from "../../helpers/clone.helper";
import * as jstz from 'jstimezonedetect';

@autoinject
export class RequestClientService {

    public static knownExceptions = [
        "WorkflowAssignmentHasOwnershipAlreadyException",
        "ScopeException",
        "DeleteRoleException",
        "NoManagerRoleException",
        "UserHasNoPermissionException",
        "DocumentsRequiredMissedException",
        "GenesisException",
        "RedirectException",
        "DuplicateRequestException",
        "MatchVesselsException"
    ];

    public apisWithoutRedirectToLogin = [
        "api/v1/me"
    ];

    constructor(
        private httpClient: HttpClient,
        private configurationService: ConfigurationService,
        private cacheClient: CacheClientService,
        private notificationClientService: NotificationClientService,
        private configService: ConfigService
    ) {
        this.loadConfigurationHeaders();
        RequestBuilder.addHelper('genesisProcessResponseType', () => {
            return (
                (client: HttpClient, processor: RequestMessageProcessor) => {

                    const arr: any[] = processor["xhrTransformers"];

                    arr.push((client: HttpClient, processor: RequestMessageProcessor, message: RequestMessage, request: XMLHttpRequest) => {
                        const oldOnreadystatechange = request.onreadystatechange;
                        request.onreadystatechange = function (...args) {
                            if (oldOnreadystatechange) {
                                oldOnreadystatechange.apply(request, args);
                            }

                            if (request.readyState == 2) {
                                if (request.status != 200) {
                                    request.responseType = 'text';
                                }
                            }
                        }
                    });
                })
        });

    }

    public loadConfigurationHeaders() {

        this.configurationService.addOrUpdate({
            key: "Access-Control-Allow-Origin",
            value: () => "*",
            type: RequestConstants.ConfigurationTypes.RequestHeaders
        });

        this.configurationService.addOrUpdate({
            key: "X-XSRF-TOKEN",
            value: () => CookieHelper.readCookie(`XSRF-TOKEN-${this.configService.config.environment.toUpperCase()}`),
            type: RequestConstants.ConfigurationTypes.RequestHeaders
        });

        this.updateRegionHeaders();
    }

    public updateRegionHeaders() {
        const setDefaultTimezone = () => {
            const config = {
                key: "Timezone",
                value: () => jstz.determine().name(),
                type: RequestConstants.ConfigurationTypes.RequestHeaders
            };
            this.configurationService.addOrUpdate(config);
        };

        this.cacheClient.get<string>(CacheKeys.timezone, CacheLocation.SessionStorage).then(x => {
            if (x) {
                this.configurationService.addOrUpdate({
                    key: "Timezone",
                    value: () => x,
                    type: RequestConstants.ConfigurationTypes.RequestHeaders
                });
            }
            else
                setDefaultTimezone()
        }, () => {
            setDefaultTimezone();
        });

        const setDefaultCulture = () => {
            this.configurationService.addOrUpdate({
                key: "CultureCode",
                value: () => window.navigator['userLanguage'] || window.navigator.language,
                type: RequestConstants.ConfigurationTypes.RequestHeaders
            });
        };

        this.cacheClient.get<string>(CacheKeys.cultureCode, CacheLocation.SessionStorage).then(x => {
            if (x) {
                this.configurationService.addOrUpdate({
                    key: "CultureCode",
                    value: () => x,
                    type: RequestConstants.ConfigurationTypes.RequestHeaders
                });
            }
            else {
                setDefaultCulture();
            }
        }, () => {
            setDefaultCulture();
        });
    }

    public async get<TResponse>(uri: string, params?: { [index: string]: any }, responseType?: ResponseType): Promise<TResponse> {
        const request = this.generateRequest(uri, params, null, responseType).asGet();
        return await this.sendRequest<TResponse>(uri, request);
    }

    public async head<TResponse>(uri: string, params?: { [index: string]: any }) {
        const request = this.generateRequest(uri, params, null).asHead();
        return await this.sendRequest<TResponse>(uri, request);
    }

    public async post<TResponse>(uri: string, params?: { [index: string]: any }, data?: any, responseType?: ResponseType) {
        const request = this.generateRequest(uri, params, data, responseType).asPost();
        return await this.sendRequest<TResponse>(uri, request);
    }

    public async postWithProgressCallback<TResponse>(uri: string, progressCallback?: (event?: ProgressEvent) => void, params?: { [index: string]: any }, data?: any, responseType?: ResponseType) {
        const request = this.generateRequest(uri, params, data, responseType).asPost();
        // set progress callback
        if (progressCallback) {
            request.withProgressCallback(progressCallback);
        }
        return await this.sendRequest<TResponse>(uri, request);
    }

    public async put<TResponse>(uri: string, params?: { [index: string]: any }, data?: any, responseType?: ResponseType) {
        const request = this.generateRequest(uri, params, data, responseType).asPut();
        return await this.sendRequest<TResponse>(uri, request);
    }

    public async delete<TResponse>(uri: string, params?: { [index: string]: any }, data?: any, responseType?: ResponseType) {
        const request = this.generateRequest(uri, params, data, responseType).asDelete();
        return await this.sendRequest<TResponse>(uri, request);
    }

    public async postFormData<TResponse>(uri: string, requestModel?: any) {
        requestModel = CloneHelper.cloneObject(requestModel);
        const data = this.appendToFormData(requestModel);
        return await this.post<TResponse>(uri, {}, data);
    }

    appendToFormData(object: any, formData: FormData = null, preKey: string = null) {
        let levelOne = false;
        if (formData == null) {
            formData = new FormData();
            levelOne = true;
        }
        const keys = Object.keys(object);
        keys.forEach(key => {
            const value = object[key];
            if (Array.isArray(value)) {
                for (let index = value.length - 1; index >= 0; index--) {
                    const element = value[index];
                    if (element instanceof File || element instanceof Blob) {
                        formData.append((preKey == null ? key : `${preKey}[${key}]`), element);
                        value.splice(index, 1);
                    } else {
                        this.appendToFormData(element, formData, (preKey == null ? key : `${preKey}[${key}]`) + `[${index}]`);
                    }
                }
            } else {
                if (value instanceof File || value instanceof Blob) {
                    formData.append((preKey == null ? key : `${preKey}[${key}]`), value);
                    delete object[key];
                }
            }
            if (levelOne && key in object) {
                formData.append(key, JSON.stringify(value));
            }
        });

        return formData;
    }

    private getApiCallTemplate(urlIsAbsolutePath?: boolean): ((x) => string) {

        if (urlIsAbsolutePath) {
            return x => x;
        }
        return x => `${this.configService.config.urls.rightShipPlatformApiUrl}/api/v1/${x}`;
    }

    private getRequestHeaders(scope?: string): { [index: string]: string } {
        return this.configurationService.getList(RequestConstants.ConfigurationTypes.RequestHeaders, scope);
    }

    private joinUriWithParams(uri: string, params: { [index: string]: any }): string {
        let queryString = (params) ? buildQueryString(params) : "";
        queryString = queryString === "" ? "" : "?" + queryString;
        queryString = queryString.replace(new RegExp("%5B%5D", "g"), "");
        return uri + queryString;
    }

    private generateUrl(uri: string, params: { [index: string]: any }): string {
        const absolutePathPattern = /^(http|https):\/\//i;
        const urlIsAbsolutePath = absolutePathPattern.test(uri);
        const apiBaseTemplate = this.getApiCallTemplate(urlIsAbsolutePath);
        uri = this.joinUriWithParams(uri, params);
        return apiBaseTemplate(uri);
    }

    private generateRequest(uri: string, params: { [index: string]: any }, body: any, responseType?: ResponseType): RequestBuilder {
        const url = this.generateUrl(uri, params);
        const headers = this.getRequestHeaders();
        const request = this.httpClient.createRequest(url);
        request["genesisProcessResponseType"]();

        if (headers) {
            for (const header in headers) {
                if (header in headers) {
                    request.withHeader(header, headers[header]);
                }
            }
        }
        if (body != null) {
            request.withContent(body);
        }

        if (responseType) {
            request.withResponseType(responseType);
        }

        request.withCredentials(true);

        return request;
    }

    private async sendRequest<TResponse>(uri: string, request: RequestBuilder): Promise<TResponse> {
        try {
            const response = await request.send();

            let result: TResponse;
            try {
                result = response.content;
            } catch (error) {
                if (response.response && response.mimeType === "application/json") {
                    result = JSON.parse(response.response);
                } else if (response.response && response.mimeType === "text/html") {
                    result = response.response;
                } else {
                    result = null;
                }
            }
            return result;

        } catch (errResponse) {

            if (errResponse?.content?.exception == "RedirectException") {
                location.href = location.origin + "/statuscode" + errResponse.statusCode;
                return;
            }
            else if (errResponse?.statusCode == 413) {
                this.notificationClientService.show("Error processing request", "The request or files being uploaded exceed the maximum upload size", NotificationType.Error);
            }
            else if (errResponse?.content?.exception && RequestClientService.knownExceptions.indexOf(errResponse.content.exception) != -1) {
                this.showKnowException(errResponse.content);
            }
            throw errResponse;
        }
    }

    public static isExceptionHandled(r) {
        return r.content && r.content.exception && RequestClientService.knownExceptions.indexOf(r.content.exception) != -1;
    }

    public downloadFile(blob: Blob, fileNameWithoutExtension: string) {
        const url = window.URL.createObjectURL(blob);
        FileHelper.downloadFile(url, fileNameWithoutExtension);
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        });
    }

    private showKnowException(item: any) {
        //DO NOT MERGE WITH GENESIS
        if (item.key != "password-expired") {
            this.notificationClientService.show("Error", item.details, NotificationType.Error);
        }
    }
}

export enum ResponseType {
    DOMString = 'text',
    ArrayBuffer = 'arraybuffer',
    Blob = 'blob',
    Document = 'document',
    JObject = 'json',
}
